/**
 * 订单详情抽屉 - 改派客户区块
 *
 * 支出订单被对方审核拒绝后，「这张单不是我的」，唯一的闭环是换个注册用户重提。
 * 入口开关 `canReassign` 来自后端投影（四端唯一判定），本组件不拼条件。
 * 受控组件，不持有业务状态。
 */
import type { OrderItem } from '../../../../../types';

type Candidate = NonNullable<OrderItem['customer']>;

interface Props {
  canReassign: boolean;
  currentCustomerName: string | null;
  open: boolean;
  search: string;
  candidates: Candidate[];
  selectedId: string;
  loading: boolean;
  saving: boolean;
  error: string;
  onOpen: () => void;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onPick: (id: string) => void;
  onConfirm: () => void;
}

export default function ReassignSection({
  canReassign,
  currentCustomerName,
  open,
  search,
  candidates,
  selectedId,
  loading,
  saving,
  error,
  onOpen,
  onClose,
  onSearchChange,
  onPick,
  onConfirm,
}: Props) {
  if (!canReassign) return null;

  return (
    <>
      {/* 改派客户：仅后端判定「支出订单 + 审核已拒绝 + 非人员调用派生 + 本人为下单方」时出现 */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">改派客户</p>
        {!open ? (
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4">
            <p className="text-sm text-violet-700">
              对方已拒绝此订单。可改派给其他客户重新提审{currentCustomerName ? `（当前：${currentCustomerName}）` : ''}。
            </p>
            <button onClick={onOpen} className="mt-3 w-full py-2 text-sm bg-violet-500 text-white rounded-lg hover:bg-violet-600 cursor-pointer transition-colors flex items-center justify-center gap-1.5">
              <i className="ri-swap-box-line text-sm" />选择新客户
            </button>
          </div>
        ) : (
          <div className="border border-violet-100 rounded-xl p-4 space-y-3">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm" />
              </div>
              <input
                type="text"
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder="搜索客户姓名 / 公司 / 电话"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
              />
            </div>
            <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-lg">
              {loading ? (
                <p className="px-4 py-3 text-sm text-gray-400 text-center">客户列表加载中…</p>
              ) : candidates.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400 text-center">未找到可改派的客户</p>
              ) : (
                candidates.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onPick(c.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${selectedId === c.id ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0 overflow-hidden">
                      {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover object-top" /> : <span className="text-violet-600 text-xs font-semibold">{(c.fullName ?? '?').charAt(0)}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{c.fullName}</p>
                      {c.companyName && <p className="text-xs text-gray-400 truncate">{c.companyName}</p>}
                    </div>
                    {c.phone && <p className="ml-auto text-xs text-gray-400 shrink-0">{c.phone}</p>}
                    {selectedId === c.id && <i className="ri-check-line text-violet-500 text-base shrink-0" />}
                  </button>
                ))
              )}
            </div>
            {error && <p className="text-xs text-rose-500">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors">取消</button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={!selectedId || saving}
                className="flex-1 py-2 text-sm bg-violet-500 text-white rounded-lg hover:bg-violet-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm" />}确认改派
              </button>
            </div>
            <p className="text-xs text-gray-400">改派后本单重新进入待审核，原客户方的待办通知同时作废。</p>
          </div>
        )}
      </div>
    </>
  );
}
